import {PaymentMethodValue, PublicFormField} from "./types";
import {isDynamicAnswerValueAllowed, isDynamicAnswerValueCompatible, isDynamicAnswerValueMissing, isValidPublicRegistrationLinkedInUrl, isValidPublicRegistrationPhone, normalizePublicFormFieldType, trimValue} from "./publicRegistrationModel";

export const requiredTextRules = (labels: Record<string, string>) => [
    {required: true, whitespace: true, message: labels.required}
];

export const requiredEmailRules = (labels: Record<string, string>) => [
    ...requiredTextRules(labels),
    {type: "email" as const, message: labels.invalidEmail}
];

export const requiredPhoneRules = (labels: Record<string, string>) => [
    ...requiredTextRules(labels),
    {
        validator: async (_: unknown, value: unknown) => {
            const serializedValue = String(value ?? "");
            if (trimValue(serializedValue) && !isValidPublicRegistrationPhone(serializedValue)) {
                throw new Error(labels.invalidPhone);
            }
        }
    }
];

export const optionalLinkedInUrlRules = (labels: Record<string, string>) => [
    {
        validator: async (_: unknown, value: unknown) => {
            if (!isValidPublicRegistrationLinkedInUrl(String(value ?? ""))) {
                throw new Error(labels.invalidLinkedInUrl);
            }
        }
    }
];

const dynamicAnswerErrorMessage = (
    field: PublicFormField,
    labels: Record<string, string>
): string => {
    switch (normalizePublicFormFieldType(field.type)) {
    case "Number":
        return labels.invalidNumber;
    case "Date":
        return labels.invalidDate;
    case "Email":
        return labels.invalidEmail;
    case "Phone":
        return labels.invalidPhone;
    case "Checkbox":
    case "Select":
    case "MultiSelect":
        return labels.invalidSelection;
    case "Text":
    case "TextArea":
    default:
        return labels.required;
    }
};

export const buildDynamicAnswerRules = (
    field: PublicFormField,
    labels: Record<string, string>
) => [
    {
        validator: async (_: unknown, value: unknown) => {
            if (field.isRequired && isDynamicAnswerValueMissing(field, value)) {
                throw new Error(labels.required);
            }

            if (!isDynamicAnswerValueCompatible(field, value) || !isDynamicAnswerValueAllowed(field, value)) {
                throw new Error(dynamicAnswerErrorMessage(field, labels));
            }
        }
    }
];

export const buildPaymentMethodRules = (
    allowedMethods: PaymentMethodValue[],
    labels: Record<string, string>
) => [
    {required: true, message: labels.required},
    {
        validator: async (_: unknown, value: unknown) => {
            if (value === undefined || value === null || value === "") {
                return;
            }

            if (!allowedMethods.includes(Number(value) as PaymentMethodValue)) {
                throw new Error(labels.invalidPaymentMethod);
            }
        }
    }
];

export const privacyConsentRules = (labels: Record<string, string>) => [
    {
        validator: async (_: unknown, value: unknown) => {
            if (value !== true) {
                throw new Error(labels.privacyConsentRequired);
            }
        }
    }
];
